import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import _ from 'lodash'

import DiagnosticMedicalVideo, { DiagnosticMedicalVideoProps } from '@/pages/diagnostic/components/DiagnosticMedicalVideo'
import { DiagnosticRTCProvider, DiagnosticRTCServiceType } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { documentOfflineStore, consultationOfflineStore, OfflineConsultation } from '@/core/offline'

// Screenshot prefix for medical video (same as online)
const SCREENSHOT_PREFIX = 'medical-screenshot'

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
            const result = reader.result as string
            // Remove the data URL prefix to get just the base64 data
            resolve(result.split(',')[1])
        }
        reader.onerror = error => reject(error)
    })
}

export type OfflineDiagnosticMedicalVideoContainerProps = Pick<DiagnosticMedicalVideoProps, 'disableStreamOnMobile' | 'videoHeight' | 'videoWidth'> & {
    consultation: OfflineConsultation
    onConsultationUpdate?: (consultation: OfflineConsultation) => void
}

export function OfflineDiagnosticMedicalVideoContainer(props: OfflineDiagnosticMedicalVideoContainerProps) {
    const { consultation, onConsultationUpdate, disableStreamOnMobile = true, ...rest } = props
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    // Use local state to track screenshot count to prevent re-rendering issues
    const [localScreenshotCount, setLocalScreenshotCount] = useState(0)
    const [pendingUploads, setPendingUploads] = useState(0)

    // Count screenshots in consultation files (same logic as online)
    const screenshotCount = useMemo(() => {
        if (!consultation.files) return localScreenshotCount

        const count = _.filter(
            consultation.files,
            (file) => file.filename.startsWith(SCREENSHOT_PREFIX) && _.some(['png', 'jpg', 'jpeg'], (ext) => file.filename.endsWith(ext))
        ).length

        return count + pendingUploads
    }, [consultation.files, localScreenshotCount, pendingUploads])

    // Update local count when consultation files change
    useEffect(() => {
        if (!consultation.files) return

        const actualCount = _.filter(
            consultation.files,
            (file) => file.filename.startsWith(SCREENSHOT_PREFIX) && _.some(['png', 'jpg', 'jpeg'], (ext) => file.filename.endsWith(ext))
        ).length

        setLocalScreenshotCount(actualCount)
    }, [consultation.files])

    const handleUploadDocument = useCallback(async (documentFile: File) => {
        if (!consultation._id) return

        try {
            // Optimistically update pending uploads count if it's a screenshot
            if (documentFile.name.startsWith(SCREENSHOT_PREFIX)) {
                setPendingUploads(prev => prev + 1)
            }

            // Convert file to base64
            const base64Data = await fileToBase64(documentFile)

            // Create offline document
            await documentOfflineStore.createOfflineDocument({
                filename: documentFile.name,
                size: documentFile.size,
                mimeType: documentFile.type,
                base64Data: base64Data,
                consultationId: consultation._id
            })

            enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })

            // Update consultation data
            if (onConsultationUpdate) {
                const updatedConsultation = await consultationOfflineStore.get(consultation._id)
                if (updatedConsultation) {
                    onConsultationUpdate(updatedConsultation)
                }
            }

            // Reset pending uploads count
            if (documentFile.name.startsWith(SCREENSHOT_PREFIX)) {
                setPendingUploads(prev => Math.max(0, prev - 1))
            }
        } catch (error) {
            console.error('Error uploading document:', error)
            enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' })

            // Revert optimistic update on error
            if (documentFile.name.startsWith(SCREENSHOT_PREFIX)) {
                setPendingUploads(prev => Math.max(0, prev - 1))
            }
        }
    }, [consultation._id, onConsultationUpdate, enqueueSnackbar, t])

    const handleToggleStreamingOnMobile = useCallback((isStreamingOnMobile: boolean) => {
        // In offline mode, mobile streaming is not available
        enqueueSnackbar('Mobile streaming is not available in offline mode', { variant: 'info' })
    }, [enqueueSnackbar])

    // Configure offline RTC provider
    const rtcConfig = {
        [DiagnosticRTCServiceType.Offline]: {}
    }

    return (
        <DiagnosticRTCProvider
            config={rtcConfig}
            customDevices={[]} // Could be populated with custom medical devices
            isStreamer={true} // In offline mode, we're always the streamer
            type="video"
            onRecordingStop={handleUploadDocument}
        >
            <DiagnosticMedicalVideo
                {...rest}
                consultation={consultation}
                disableStreamOnMobile={disableStreamOnMobile}
                onRecordStop={handleUploadDocument}
                onScreenshot={handleUploadDocument}
                onSaveFreezedPhoto={handleUploadDocument}
                onSaveFreezedVideo={handleUploadDocument}
                onToggleStreamingOnMobile={handleToggleStreamingOnMobile}
                streamingOnMobileLinkQrCode={undefined}
                isLoadingQrCodeMobile={false}
                screenshotCount={screenshotCount}
            />
        </DiagnosticRTCProvider>
    )
}

export default OfflineDiagnosticMedicalVideoContainer 